import { Box, Typography, useTheme } from '@mui/material';
import dayjs from 'dayjs';

import { QuestionReviewProps } from '@/models/QuestionReview';

interface CommentThreadCardProps {
  chat: QuestionReviewProps;
  accentColor: string;
  isCurrentUserComment: boolean;
  reviewerUserTypeLabel: string | null;
  userTimeZone: string;
  resolved: boolean;
}

function CommentThreadCard({
  chat,
  accentColor,
  isCurrentUserComment,
  reviewerUserTypeLabel,
  userTimeZone,
  resolved,
}: CommentThreadCardProps) {
  const theme = useTheme();
  const reviewerName = chat.reviewerName?.trim() ?? 'Reviewer';
  const reviewerNotes = chat.reviewerNotes?.trim();

  return (
    <Box
      sx={{
        marginBottom: '10px',
        borderRadius: '8px',
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: isCurrentUserComment
          ? `inset 4px 0 0 0 ${theme.palette.info.main}`
          : `inset -4px 0 0 0 ${accentColor}`,
        backgroundColor: resolved ? theme.palette.action.hover : theme.palette.common.white,
        padding: '10px',
        opacity: resolved ? 0.82 : 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '10px',
          marginBottom: '6px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            flexWrap: 'wrap',
            minWidth: 0,
          }}
        >
          <Typography variant='subtitle3' fontWeight='bold'>
            {reviewerName}
          </Typography>
          {isCurrentUserComment && (
            <Typography
              variant='subtitle3'
              color={theme.palette.info.main}
              sx={{ fontSize: '0.75rem' }}
            >
              (You)
            </Typography>
          )}
          {!isCurrentUserComment && reviewerUserTypeLabel != null && (
            <Typography
              variant='subtitle3'
              color={theme.palette.text.secondary}
              sx={{
                fontSize: '0.7rem',
                backgroundColor: theme.palette.action.selected,
                borderRadius: '10px',
                padding: '1px 6px',
              }}
            >
              {reviewerUserTypeLabel}
            </Typography>
          )}
        </Box>
      </Box>

      <Typography sx={{ padding: '4px 0px' }}>
        {reviewerNotes != null && reviewerNotes.length > 0
          ? reviewerNotes
          : 'No comment text provided.'}
      </Typography>

      <Typography
        variant='subtitle3'
        color={theme.palette.text.secondary}
        sx={{ fontSize: '0.75rem' }}
      >
        {dayjs.utc(chat.reviewDate).tz(userTimeZone).format('MM/DD/YYYY h:mm A')}
      </Typography>
    </Box>
  );
}

export default CommentThreadCard;
